/**
 * Country Phone Metadata
 * Contains phone number format information for countries
 */

import type { CountryCode } from 'libphonenumber-js';
import type { CountryPhoneInfo } from '../types';

/**
 * Country phone metadata
 * Contains expected digit counts (excluding country code) and format examples
 */
export const COUNTRY_PHONE_INFO: Record<string, CountryPhoneInfo> = {
  // North America
  US: { digits: 10, format: '(XXX) XXX-XXXX' },
  CA: { digits: 10, format: '(XXX) XXX-XXXX' },
  MX: { digits: 10, format: 'XX XXXX XXXX' },

  // Europe - Western
  GB: { digits: [10, 11], format: 'XXXX XXX XXXX' },
  DE: { digits: [10, 11], format: 'XXXX XXXXXXX' },
  FR: { digits: 9, format: 'X XX XX XX XX' },
  IT: { digits: [9, 10], format: 'XXX XXX XXXX' },
  ES: { digits: 9, format: 'XXX XX XX XX' },
  NL: { digits: 9, format: 'XX XXX XXXX' },
  BE: { digits: 9, format: 'XXX XX XX XX' },
  CH: { digits: 9, format: 'XX XXX XX XX' },
  AT: { digits: [10, 13], format: 'XXX XXXXXXX' },
  IE: { digits: [9, 10], format: 'XX XXX XXXX' },
  PT: { digits: 9, format: 'XXX XXX XXX' },
  LU: { digits: [8, 9], format: 'XXX XXX XXX' },

  // Europe - Nordic
  SE: { digits: [9, 10], format: 'XX XXX XX XX' },
  NO: { digits: 8, format: 'XXX XX XXX' },
  DK: { digits: 8, format: 'XX XX XX XX' },
  FI: { digits: [9, 10], format: 'XX XXX XXXX' },

  // Europe - Eastern
  PL: { digits: 9, format: 'XXX XXX XXX' },
  CZ: { digits: 9, format: 'XXX XXX XXX' },
  HU: { digits: 9, format: 'XX XXX XXXX' },
  RO: { digits: 9, format: 'XXX XXX XXX' },
  BG: { digits: [8, 9], format: 'XX XXX XXXX' },
  HR: { digits: [8, 9], format: 'XX XXX XXXX' },
  SK: { digits: 9, format: 'XXX XXX XXX' },
  SI: { digits: 8, format: 'XX XXX XXX' },
  RS: { digits: [8, 9], format: 'XX XXX XXXX' },
  UA: { digits: 9, format: 'XX XXX XX XX' },
  RU: { digits: 10, format: 'XXX XXX-XX-XX' },
  GR: { digits: 10, format: 'XXX XXX XXXX' },
  TR: { digits: 10, format: 'XXX XXX XX XX' },

  // Asia - South
  IN: { digits: 10, format: 'XXXXX XXXXX' },
  PK: { digits: 10, format: 'XXX XXXXXXX' },
  BD: { digits: 10, format: 'XXXX XXXXXX' },
  LK: { digits: 9, format: 'XX XXX XXXX' },
  NP: { digits: 10, format: 'XXX XXXXXXX' },

  // Asia - East
  CN: { digits: 11, format: 'XXX XXXX XXXX' },
  JP: { digits: [10, 11], format: 'XX XXXX XXXX' },
  KR: { digits: [10, 11], format: 'XX XXXX XXXX' },
  TW: { digits: 9, format: 'XXX XXX XXX' },
  HK: { digits: 8, format: 'XXXX XXXX' },

  // Asia - Southeast
  ID: { digits: [10, 12], format: 'XXX XXXX XXXX' },
  MY: { digits: [9, 10], format: 'XX XXXX XXXX' },
  SG: { digits: 8, format: 'XXXX XXXX' },
  TH: { digits: 9, format: 'XX XXX XXXX' },
  VN: { digits: [9, 10], format: 'XXX XXX XXXX' },
  PH: { digits: 10, format: 'XXX XXX XXXX' },

  // Middle East
  AE: { digits: 9, format: 'XX XXX XXXX' },
  SA: { digits: 9, format: 'XX XXX XXXX' },
  EG: { digits: 10, format: 'XX XXXX XXXX' },
  IQ: { digits: 10, format: 'XXX XXX XXXX' },
  JO: { digits: 9, format: 'X XXXX XXXX' },
  LB: { digits: [7, 8], format: 'XX XXX XXX' },
  KW: { digits: 8, format: 'XXXX XXXX' },
  QA: { digits: 8, format: 'XXXX XXXX' },
  BH: { digits: 8, format: 'XXXX XXXX' },
  OM: { digits: 8, format: 'XXXX XXXX' },
  YE: { digits: 9, format: 'XXX XXX XXX' },
  PS: { digits: 9, format: 'XXX XXX XXX' },
  IL: { digits: 9, format: 'XX XXX XXXX' },

  // Africa
  ZA: { digits: 9, format: 'XX XXX XXXX' },
  NG: { digits: 10, format: 'XXX XXX XXXX' },
  KE: { digits: 9, format: 'XXX XXXXXX' },
  GH: { digits: 9, format: 'XX XXX XXXX' },
  MA: { digits: 9, format: 'XXX XXX XXX' },
  DZ: { digits: 9, format: 'XXX XX XX XX' },
  TN: { digits: 8, format: 'XX XXX XXX' },
  LY: { digits: 9, format: 'XX XXX XXXX' },

  // South America
  BR: { digits: [10, 11], format: 'XX XXXXX XXXX' },
  AR: { digits: 10, format: 'XX XXXX XXXX' },
  CO: { digits: 10, format: 'XXX XXX XXXX' },
  CL: { digits: 9, format: 'X XXXX XXXX' },
  PE: { digits: 9, format: 'XXX XXX XXX' },
  VE: { digits: 10, format: 'XXX XXX XXXX' },

  // Oceania
  AU: { digits: 9, format: 'XXX XXX XXX' },
  NZ: { digits: [8, 10], format: 'XX XXX XXXX' },
};

/**
 * Calling code to country mapping
 * For smart prefix detection (e.g., +62 → Indonesia)
 *
 * Note: Some calling codes are shared (e.g., +1 for US/CA)
 * In those cases, we prefer the more populous country
 */
export const CALLING_CODE_TO_COUNTRY: Record<string, CountryCode> = {
  // Single-digit codes
  '1': 'US', // US, Canada share +1

  // Two-digit codes
  '20': 'EG',
  '27': 'ZA',
  '30': 'GR',
  '31': 'NL',
  '32': 'BE',
  '33': 'FR',
  '34': 'ES',
  '36': 'HU',
  '39': 'IT',
  '40': 'RO',
  '41': 'CH',
  '43': 'AT',
  '44': 'GB',
  '45': 'DK',
  '46': 'SE',
  '47': 'NO',
  '48': 'PL',
  '49': 'DE',
  '51': 'PE',
  '52': 'MX',
  '53': 'CU',
  '54': 'AR',
  '55': 'BR',
  '56': 'CL',
  '57': 'CO',
  '58': 'VE',
  '60': 'MY',
  '61': 'AU',
  '62': 'ID',
  '63': 'PH',
  '64': 'NZ',
  '65': 'SG',
  '66': 'TH',
  '81': 'JP',
  '82': 'KR',
  '84': 'VN',
  '86': 'CN',
  '90': 'TR',
  '91': 'IN',
  '92': 'PK',
  '93': 'AF',
  '94': 'LK',
  '95': 'MM',
  '98': 'IR',

  // Three-digit codes
  '211': 'SS',
  '212': 'MA',
  '213': 'DZ',
  '216': 'TN',
  '218': 'LY',
  '220': 'GM',
  '221': 'SN',
  '222': 'MR',
  '223': 'ML',
  '224': 'GN',
  '225': 'CI',
  '226': 'BF',
  '227': 'NE',
  '228': 'TG',
  '229': 'BJ',
  '230': 'MU',
  '231': 'LR',
  '232': 'SL',
  '233': 'GH',
  '234': 'NG',
  '235': 'TD',
  '236': 'CF',
  '237': 'CM',
  '238': 'CV',
  '239': 'ST',
  '240': 'GQ',
  '241': 'GA',
  '242': 'CG',
  '243': 'CD',
  '244': 'AO',
  '245': 'GW',
  '246': 'IO',
  '247': 'AC',
  '248': 'SC',
  '249': 'SD',
  '250': 'RW',
  '251': 'ET',
  '252': 'SO',
  '253': 'DJ',
  '254': 'KE',
  '255': 'TZ',
  '256': 'UG',
  '257': 'BI',
  '258': 'MZ',
  '260': 'ZM',
  '261': 'MG',
  '262': 'RE',
  '263': 'ZW',
  '264': 'NA',
  '265': 'MW',
  '266': 'LS',
  '267': 'BW',
  '268': 'SZ',
  '269': 'KM',
  '290': 'SH',
  '291': 'ER',
  '297': 'AW',
  '298': 'FO',
  '299': 'GL',
  '350': 'GI',
  '351': 'PT',
  '352': 'LU',
  '353': 'IE',
  '354': 'IS',
  '355': 'AL',
  '356': 'MT',
  '357': 'CY',
  '358': 'FI',
  '359': 'BG',
  '370': 'LT',
  '371': 'LV',
  '372': 'EE',
  '373': 'MD',
  '374': 'AM',
  '375': 'BY',
  '376': 'AD',
  '377': 'MC',
  '378': 'SM',
  '380': 'UA',
  '381': 'RS',
  '382': 'ME',
  '383': 'XK',
  '385': 'HR',
  '386': 'SI',
  '387': 'BA',
  '389': 'MK',
  '420': 'CZ',
  '421': 'SK',
  '423': 'LI',
  '852': 'HK',
  '853': 'MO',
  '855': 'KH',
  '856': 'LA',
  '880': 'BD',
  '886': 'TW',
  '960': 'MV',
  '961': 'LB',
  '962': 'JO',
  '963': 'SY',
  '964': 'IQ',
  '965': 'KW',
  '966': 'SA',
  '967': 'YE',
  '968': 'OM',
  '970': 'PS',
  '971': 'AE',
  '972': 'IL',
  '973': 'BH',
  '974': 'QA',
  '975': 'BT',
  '976': 'MN',
  '977': 'NP',
};

/**
 * Get country phone info
 * Returns undefined if country not in our metadata
 */
export function getCountryInfo(countryCode: string): CountryPhoneInfo | undefined {
  return COUNTRY_PHONE_INFO[countryCode];
}

/**
 * Get expected digit range for a country
 */
export function getExpectedDigits(countryCode: string): { min: number; max: number } {
  const info = COUNTRY_PHONE_INFO[countryCode];
  if (!info) {
    return { min: 7, max: 15 }; // ITU-T E.164 limits
  }

  if (Array.isArray(info.digits)) {
    return { min: info.digits[0], max: info.digits[1] };
  }

  return { min: info.digits, max: info.digits };
}

/**
 * Detect country from calling code prefix
 * Returns null if no match or prefix is ambiguous
 */
export function detectCountryFromCallingCode(callingCode: string): CountryCode | null {
  // Try exact match first
  const exactMatch = CALLING_CODE_TO_COUNTRY[callingCode];
  if (exactMatch) return exactMatch;

  return null;
}

/**
 * Get country name for display
 */
export function getCountryName(countryCode: CountryCode): string {
  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  try {
    return regionNames.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
}

/**
 * Detect user's country from browser locale/timezone
 * Falls back to 'US' if detection fails
 */
export function detectUserCountry(): CountryCode {
  try {
    // Method 1: Get from navigator.language (e.g., 'en-US', 'bn-BD')
    const locale = navigator.language || '';
    const localeParts = locale.split('-');
    if (localeParts.length > 1) {
      const countryFromLocale = localeParts[1].toUpperCase();
      if (COUNTRY_PHONE_INFO[countryFromLocale]) {
        return countryFromLocale as CountryCode;
      }
    }

    // Method 2: Infer from timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneCountryMap: Record<string, CountryCode> = {
      'Asia/Dhaka': 'BD',
      'Asia/Karachi': 'PK',
      'Asia/Kolkata': 'IN',
      'Asia/Dubai': 'AE',
      'Asia/Riyadh': 'SA',
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Toronto': 'CA',
      'Australia/Sydney': 'AU',
      'Asia/Singapore': 'SG',
      'Asia/Tokyo': 'JP',
      'Asia/Seoul': 'KR',
      'Asia/Jakarta': 'ID',
      'Asia/Kuala_Lumpur': 'MY',
      'Asia/Ho_Chi_Minh': 'VN',
      'Asia/Manila': 'PH',
      'Asia/Bangkok': 'TH',
    };

    if (timezone && timezoneCountryMap[timezone]) {
      return timezoneCountryMap[timezone];
    }

    return 'US';
  } catch {
    return 'US';
  }
}
